import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { adminApi } from '../../api/clients';
import { approvalNote, isApproved, isFinished } from '../../utils/approvalSummary';
import { seesFamilyDetail } from '../../utils/adminSession';
import { colour, pillSx, primaryButtonSx } from '../admin/tokens';
import ChangeList from './ChangeList';
import ReviewImage from './ReviewImage';
import ReviewDocuments from './ReviewDocuments';
import ContactDetails from './ContactDetails';
import FamilySummary from './FamilySummary';
import JourneyPanel from './JourneyPanel';
import StudentTabs from './StudentTabs';
import FamilyComments from './FamilyComments';

/**
 * One family's re-enrolment, in the shape a reviewer reads it.
 *
 * The family is at the top — who they are, what they paid, where TASS has got to, what they
 * changed and which cards they sent — and each child has a tab below. Everything a decision
 * needs is in one place; before this, the money and the sync status were on other screens or on
 * nobody's screen.
 *
 * The enrolment team and the accounts team see different halves of it, and the halves are
 * decided by what the server sent rather than by asking again here: a section whose field is
 * missing is a section that is not this person's job. The one exception is the note in the
 * footer, which has to say which of the two reasons the Approve button is absent for.
 */
const ReviewChangesDialog = ({
    row, open, onClose, onApprove, approving = false, error = '', canApprove = true, onCommentsChange,
}) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (!open || !row) {
            setDetail(null);
            setLoadError('');
            return undefined;
        }

        let ignore = false;
        setLoading(true);
        setLoadError('');

        adminApi
            .get('/fetchParentChangeDetail', {
                params: { SessionID: row.id, ParentID: row.parent_id },
            })
            .then(({ data }) => {
                if (!ignore) setDetail(data);
            })
            .catch(() => {
                // Approving without seeing the change would defeat the point, so say so rather
                // than showing an empty review.
                if (!ignore) setLoadError('Could not load what changed. Please try again.');
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });

        return () => { ignore = true; };
    }, [open, row]);

    if (!row) return null;

    const alreadyApproved = isApproved(row);
    const onTheChanges = seesFamilyDetail();
    // The detail is the fresher answer; the row is what the list had when it was fetched.
    const finished = isFinished({ sync: detail?.sync || row.sync });
    const students = detail?.students || [];
    const images = detail?.images || [];
    // The family's own cards — a child's card belongs on that child's tab.
    const familyImages = images.filter((image) => !image.student_id);
    const contactChanges = detail?.contact || [];
    // Only meaningful to whoever is being shown the changes. For accounts there is no "nothing
    // changed" to report, because they were never shown what did.
    const nothingChanged = !loading && !loadError
        && detail?.contact !== undefined
        && !contactChanges.length
        && !students.some((student) => student.changes?.length)
        && !images.length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={fullScreen}
            PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '16px' } }}
        >
            {/* The dark header the parent-facing dialogs use, so a reviewer is looking at the
                same application a parent is. */}
            <Box sx={{
                bgcolor: colour.ink,
                color: colour.stage,
                px: { xs: 2, sm: 3 },
                py: 2,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
            }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: { xs: '17px', sm: '19px' }, fontWeight: 800, letterSpacing: '-0.01em' }}>
                        {row.family_name || 'Family'}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: colour.faint, mt: 0.25 }}>
                        Parent code {row.parent_id} · {row.college}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {row.needs_approval === false && (
                        <Chip size="small" label="No approval needed" sx={pillSx('neutral')} />
                    )}
                    <IconButton onClick={onClose} sx={{ color: colour.faint }} aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert>}
                {loadError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{loadError}</Alert>}

                {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
                        <CircularProgress size={18} sx={{ color: colour.primary }} />
                        <Typography sx={{ fontSize: '14px', color: colour.muted }}>Loading this family…</Typography>
                    </Box>
                )}

                {!loading && (
                    <FamilySummary
                        family={detail?.family || {}}
                        payment={detail?.payment ?? row.payment}
                        sync={detail?.sync || row.sync || {}}
                        timing={detail?.timing || row.timing}
                        showDetailSync={onTheChanges}
                    />
                )}

                {/* Where this family stopped. The list row already carries it, so the panel is
                    there before the detail request comes back — and the change-detail endpoint
                    does not send it, which is why this reads the row and not `detail`. */}
                {!loading && <JourneyPanel journey={row.journey} />}

                {/* Where to reach them. Shown to both teams: accounts chase fees by ringing
                    somebody, and the enrolment team are looking at the same family. */}
                {!loading && <ContactDetails details={detail?.contactDetails || []} />}

                {/* What AR and the enrolment team have said about this family. Both teams see every
                    comment, and anybody signed in may add one.

                    The box used to be hidden from anybody whose access level was view, which hid it
                    from eight of sixteen active staff — five of the six on accounts, who are the
                    people who ring a family about a declined card. A comment approves nothing and
                    moves no money, so the level that stops them changing a family's record should
                    not stop them telling a colleague what happened. The name on a comment comes
                    from the sign-in and cannot be typed, so it still says who wrote it.

                    The Approve button below is a different matter and is still view-only gated. */}
                {!loading && (
                    <FamilyComments
                        sessionId={row.id}
                        comments={row.comments || []}
                        onChange={(comments) => onCommentsChange?.(row.id, comments)}
                    />
                )}

                {!loading && <ReviewDocuments sessionId={row.id} parentId={row.parent_id} />}

                {!loading && contactChanges.length > 0 && (
                    <Box sx={{ mb: 2.5 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: colour.body, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.25 }}>
                            Contact details they changed
                        </Typography>
                        <ChangeList changes={contactChanges} />
                    </Box>
                )}

                {!loading && familyImages.length > 0 && (
                    <Box sx={{ mb: 2.5 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: colour.body, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.25 }}>
                            {onTheChanges ? 'Cards for the family' : 'Health care card'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {familyImages.map((image, index) => (
                                <ReviewImage key={`${image.object_path}-${index}`} image={image} />
                            ))}
                        </Box>
                    </Box>
                )}

                {!loading && (
                    <Box sx={{
                        border: `1px solid ${colour.border}`,
                        borderRadius: '12px',
                        p: { xs: 1.5, sm: 2 },
                    }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: colour.body, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.25 }}>
                            Children
                        </Typography>
                        <StudentTabs students={students} images={images} />
                    </Box>
                )}

                {nothingChanged && (
                    <Alert severity="info" sx={{ mt: 2.5, borderRadius: '12px' }}>
                        Nothing differs from what the school holds.
                    </Alert>
                )}

                {alreadyApproved && (
                    <Alert severity="success" sx={{ mt: 2.5, borderRadius: '12px' }}>
                        {approvalNote(row) || 'Already approved.'}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: `1px solid ${colour.border}` }}>
                {/* View-only access is given to people who need to read a family's changes and
                    answer a question about them. Showing a button the server would refuse would
                    only look like a fault. */}
                {(!canApprove || !finished) && (
                    <Typography sx={{ fontSize: '13px', color: colour.muted, mr: 'auto' }}>
                        {!finished
                            ? 'This family has not finished their re-enrolment yet.'
                            : (onTheChanges
                                ? 'Your access is view only.'
                                : 'Approving a family\u2019s changes is the enrolment team\u2019s.')}
                    </Typography>
                )}
                <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700, color: colour.bodySoft }}>
                    Close
                </Button>
                {canApprove && finished && (
                    <Button
                        variant="contained"
                        disabled={approving || alreadyApproved || loading}
                        onClick={() => onApprove(row)}
                        sx={primaryButtonSx}
                    >
                        {approving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Approve for TASS'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ReviewChangesDialog;
